import { Switch } from "@mui/material";
import { addCustomDataGridFilters } from "../../../../../components/common/customDataGridFilters/customDataGridFilters";
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridSelectionModel,
} from "@mui/x-data-grid-pro";
import CclDataGridToolbar from "../../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import { ParticipantBuildPlanItem } from "../../../../../services/types/enterpriseParticipantApiTypes";
import React from "react";
import {
    eRoleIds,
    UserClaimsService,
} from "../../../../../services/currentUserService/currentUserService";
import { CclPersistantDataGridMiscButton } from "../../../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";

export interface AssessmentListDataGridProProps {
    handleButtonClick: (buttonId: string, selectedIds: number[]) => void;
    assessments: ParticipantBuildPlanItem[];
    loading: boolean;
    handleItemEnabledChange: (bpiaKey: number, newState: boolean) => void;
    readOnly?: boolean;
    actionInProgress: string;
}

export const AssessmentListDataGridPro: React.FC<AssessmentListDataGridProProps> = (props) => {
    const [selectedDocuments, setSelectedDocuments] = React.useState<ParticipantBuildPlanItem[]>(
        [],
    );
    const [selectedAssessments, setSelectedAssessments] = React.useState<
        ParticipantBuildPlanItem[]
    >([]);
    const claimsService = new UserClaimsService();
    const isNwaAdmin = claimsService.IsUserNwaAdmin();

    const handleButtonClick = (buttonId: string) => {
        var selectedIds =
            buttonId === "reopenassessments"
                ? selectedAssessments.map((a) => a.cclMasterKey)
                : selectedDocuments.map((d) => d.cclMasterKey);
        props.handleButtonClick(buttonId, selectedIds);
    };

    var COLUMNS: GridColDef[] = [
        {
            field: "assessmentType",
            headerName: "Type",
            type: "string",
            flex: 0.2,
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            flex: 1,
        },
        {
            field: "language",
            headerName: "Language",
            type: "string",
            flex: 0.2,
        },
        {
            field: "status",
            headerName: "Status",
            type: "string",
            flex: 0.2,
        },
        {
            field: "scoreDate",
            headerName: "Scored",
            type: "string",
            valueGetter: (params: GridRenderCellParams) => {
                if (params.row.scoreDate == null) return "Not Scored";
                else {
                    return new Date(params.row.scoreDate).toLocaleDateString();
                }
            },
            flex: 0.2,
        },
        {
            field: "cclMasterKey",
            headerName: "CCL Master Key",
            type: "string",
            flex: 0.2,
        },
        {
            field: "enabled",
            headerName: "Enabled",
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Switch
                        size="small"
                        checked={params.row.enabled}
                        disabled={props.readOnly}
                        onChange={(evt, checked) =>
                            props.handleItemEnabledChange(params.row.bpiA_Key, checked)
                        }
                    />
                );
            },
            type: "boolean",
            flex: 0.2,
        },
    ];

    if (props.readOnly) {
        COLUMNS = COLUMNS.filter((col) => col.field !== "cclMasterKey");
    }

    const viewReopen =
        claimsService.IsUserSuperAdmin() ||
        claimsService.IsUserInAppRole(eRoleIds.eAssessmentManager);

    const miscButtons: CclPersistantDataGridMiscButton[] = props.readOnly
        ? []
        : [
              {
                  id: "addassessment",
                  caption: "Add Assessment",
                  disable: false,
                  returnSelected: false,
                  restricted: false,
                  onClick: () => handleButtonClick("addassessment"),
              },
              ...(!isNwaAdmin
                  ? [
                        {
                            id: "adddocument",
                            caption: "Add Document",
                            disable: false,
                            returnSelected: false,
                            restricted: false,
                            onClick: () => handleButtonClick("adddocument"),
                        },
                        {
                            id: "downloaddocument",
                            caption: "Download Document",
                            disable: selectedDocuments.length !== 1,
                            returnSelected: true,
                            restricted: false,
                            onClick: () => handleButtonClick("downloaddocument"),
                            loadingBtn: true,
                            isLoading: props.actionInProgress === "downloaddocument",
                        },
                    ]
                  : []),
          ];

    if (viewReopen) {
        const reopenbutton: CclPersistantDataGridMiscButton = {
            id: "reopenassessments",
            caption: "Re-Open Assessment(s)",
            disable: selectedAssessments.length === 0,
            returnSelected: true,
            restricted: false,
            onClick: () => handleButtonClick("reopenassessments"),
            loadingBtn: true,
            isLoading: props.actionInProgress === "reopenassessments",
        };

        if (props.readOnly) miscButtons.push(reopenbutton);
        else miscButtons.splice(1, 0, reopenbutton);
    }

    return (
        <DataGridPro
            loading={props.loading}
            columns={addCustomDataGridFilters(COLUMNS)}
            initialState={{
                sorting: {
                    sortModel: [{ field: "title", sort: "asc" }],
                },
            }}
            rows={props.assessments}
            getRowId={(row: ParticipantBuildPlanItem) => row.bpiA_Key}
            checkboxSelection={true}
            disableSelectionOnClick={true}
            onSelectionModelChange={(ids: GridSelectionModel) => {
                const selectedIds = [...ids.map((i) => i as number)];
                setSelectedDocuments([
                    ...props.assessments?.filter(
                        (a) => selectedIds.includes(a.bpiA_Key) && a.instrumentTypeKey === 0,
                    ),
                ]);
                setSelectedAssessments([
                    ...props.assessments?.filter(
                        (a) => selectedIds.includes(a.bpiA_Key) && a.bpiT_Key === 1,
                    ),
                ]);
            }}
            components={{ Toolbar: CclDataGridToolbar }}
            componentsProps={{
                toolbar: {
                    showSaveState: true,
                    hideSettingsButtons: true,
                    miscButtons: miscButtons,
                    btnClick: handleButtonClick,
                },
            }}
            autoHeight
        />
    );
};

export default AssessmentListDataGridPro;
